import React, { useState } from 'react';
import { Container, Row, Col, Button, Card, Badge, Nav, Tab } from 'react-bootstrap';
import { 
  FaGraduationCap, FaBook, FaCalculator, FaAtom, FaFlask, 
  FaRegLightbulb, FaRegFileAlt, FaCalendarAlt, FaUniversity,
  FaMedal, FaChalkboardTeacher, FaUserGraduate, FaLaptop, 
  FaChartLine, FaBookReader, FaCertificate, FaAngleRight, FaAngleLeft
} from 'react-icons/fa';

const IITJEEPreparationPage = () => {
  // State for book carousel
  const [currentBookIndex, setCurrentBookIndex] = useState(0);

  // Reference books data
  const referenceBooks = [
    {
      id: 1,
      title: "H.C. Verma's Concepts of Physics",
      subject: "Physics",
      author: "H.C. Verma",
      description: "Comprehensive coverage of fundamental physics concepts with numerous solved examples and practice problems.",
      icon: <FaAtom />
    },
    {
      id: 2,
      title: "I.E. Irodov's Problems in General Physics",
      subject: "Physics",
      author: "I.E. Irodov",
      description: "Advanced problem collection covering mechanics, thermodynamics, electrodynamics, and more for JEE Advanced level.",
      icon: <FaAtom />
    },
    {
      id: 3,
      title: "R.D. Sharma's Mathematics",
      subject: "Mathematics",
      author: "R.D. Sharma",
      description: "Systematic approach to mathematics with well-explained concepts and graded exercise problems.",
      icon: <FaCalculator />
    },
    {
      id: 4,
      title: "Cengage Mathematics Series",
      subject: "Mathematics",
      author: "G. Tewani",
      description: "Comprehensive mathematics series with topic-wise theory and large question bank for practice.",
      icon: <FaCalculator />
    },
    {
      id: 5,
      title: "Physical Chemistry by O.P. Tandon",
      subject: "Chemistry",
      author: "O.P. Tandon",
      description: "Detailed coverage of physical chemistry with focus on numerical problems and conceptual clarity.",
      icon: <FaFlask />
    },
    {
      id: 6,
      title: "Organic Chemistry by Morrison & Boyd",
      subject: "Chemistry",
      author: "Morrison & Boyd",
      description: "In-depth coverage of organic chemistry mechanisms and reactions for advanced understanding.",
      icon: <FaFlask />
    }
  ];

  // Previous year papers
  const previousYearPapers = [
    {
      year: 2024,
      paperLink: "#",
      solutionLink: "#"
    },
    {
      year: 2023,
      paperLink: "#",
      solutionLink: "#"
    },
    {
      year: 2022,
      paperLink: "#",
      solutionLink: "#"
    },
    {
      year: 2021,
      paperLink: "#",
      solutionLink: "#"
    },
    {
      year: 2020,
      paperLink: "#",
      solutionLink: "#"
    }
  ];

  // Handle book carousel navigation
  const handlePrevBook = () => {
    setCurrentBookIndex((prevIndex) => 
      prevIndex === 0 ? referenceBooks.length - 3 : prevIndex - 1
    );
  };

  const handleNextBook = () => {
    setCurrentBookIndex((prevIndex) => 
      prevIndex === referenceBooks.length - 3 ? 0 : prevIndex + 1
    );
  };

  return (
    <div className="iitjee-page">
      {/* Hero Section */}
      <section className="hero-section py-5" style={{
        background: 'linear-gradient(135deg, rgb(0, 47, 108), rgb(0, 90, 171), rgb(0, 119, 204))',
        color: 'white',
        borderRadius: '0 0 20px 20px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h1 className="display-4 fw-bold mb-3">Master IIT JEE</h1>
              <h3 className="mb-4">Comprehensive Preparation for JEE Main & Advanced</h3>
              <p className="lead mb-4">
                Expert-designed study materials, practice tests, and personalized guidance to help you secure admission to IITs, NITs, and IIITs.
              </p>
              <div className="d-flex gap-3">
                <Button variant="light" size="lg" className="fw-bold">Enroll Now</Button>
                <Button variant="outline-light" size="lg">Download Study Plan</Button>
              </div>
            </Col>
            <Col lg={6}>
              <div className="position-relative" style={{ height: '400px' }}>
                <div className="position-absolute" style={{
                  width: '100%',
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  overflow: 'hidden'
                }}>
                  <div className="stack-animation" style={{
                    position: 'absolute',
                    top: '30%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '120px',
                    color: 'rgba(255, 255, 255, 0.8)'
                  }}>
                    <FaGraduationCap />
                  </div>
                  <div className="position-absolute" style={{
                    bottom: '20px',
                    left: '0',
                    width: '100%',
                    textAlign: 'center'
                  }}>
                    <div className="d-flex justify-content-center gap-4">
                      <FaCalculator size={50} />
                      <FaAtom size={50} />
                      <FaFlask size={50} /> 
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Programs Overview Section */}
      <section className="programs-overview py-5 mt-5">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="fw-bold">Our JEE Programs</h2>
            <p className="lead">Choose the right preparation path for your engineering dreams</p>
          </div>
          <Row>
            <Col md={6} className="mb-4">
              <Card className="h-100 program-card shadow-lg" style={{
                borderRadius: '15px',
                overflow: 'hidden',
                border: 'none',
                transition: 'transform 0.3s ease'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #002F6C, #005AB5)',
                  padding: '2rem',
                  color: 'white'
                }}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="fw-bold m-0">JEE Main Preparation</h3>
                    <FaCalendarAlt size={30} />
                  </div>
                  <Badge bg="light" text="dark" className="px-3 py-2">NITs, IIITs & GFTIs</Badge>
                </div>
                <Card.Body>
                  <ul className="program-features">
                    <li className="mb-3 d-flex align-items-center">
                      <span className="feature-icon me-3 text-primary">
                        <FaBook size={20} />
                      </span>
                      <span>Comprehensive coverage of JEE Main syllabus</span>
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <span className="feature-icon me-3 text-primary">
                        <FaRegFileAlt size={20} />
                      </span>
                      <span>12+ full-length mock tests with detailed analysis</span>
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <span className="feature-icon me-3 text-primary">
                        <FaRegLightbulb size={20} />
                      </span>
                      <span>Topic-wise practice questions and solutions</span>
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <span className="feature-icon me-3 text-primary">
                        <FaChalkboardTeacher size={20} />
                      </span>
                      <span>Live doubt clearing sessions with experts</span>
                    </li>
                  </ul>
                  <div className="text-center mt-4">
                    <h4 className="fw-bold">₹15,999</h4>
                    <Button variant="primary" className="w-100 mt-3" style={{
                      background: 'linear-gradient(135deg, #002F6C, #005AB5)',
                      border: 'none'
                    }}>Enroll Now</Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} className="mb-4">
              <Card className="h-100 program-card shadow-lg" style={{
                borderRadius: '15px',
                overflow: 'hidden',
                border: 'none',
                transition: 'transform 0.3s ease'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #001F49, #004190)',
                  padding: '2rem',
                  color: 'white'
                }}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="fw-bold m-0">JEE Advanced Preparation</h3>
                    <FaCalendarAlt size={30} />
                  </div>
                  <Badge bg="light" text="dark" className="px-3 py-2">IITs & IISC</Badge>
                </div>
                <Card.Body>
                  <ul className="program-features">
                    <li className="mb-3 d-flex align-items-center">
                      <span className="feature-icon me-3 text-primary">
                        <FaLaptop size={20} />
                      </span>
                      <span>Advanced problem-solving techniques & strategies</span>
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <span className="feature-icon me-3 text-primary">
                        <FaMedal size={20} />
                      </span>
                      <span>Coverage of JEE Advanced level topics & questions</span>
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <span className="feature-icon me-3 text-primary">
                        <FaChartLine size={20} />
                      </span>
                      <span>Personal performance tracking & improvement plan</span>
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <span className="feature-icon me-3 text-primary">
                        <FaUserGraduate size={20} />
                      </span>
                      <span>Mentoring by IIT alumni and top educators</span>
                    </li>
                  </ul>
                  <div className="text-center mt-4">
                    <h4 className="fw-bold">₹25,999</h4>
                    <Button variant="primary" className="w-100 mt-3" style={{
                      background: 'linear-gradient(135deg, #001F49, #004190)',
                      border: 'none'
                    }}>Enroll Now</Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* JEE Syllabus Section with Tabs */}
      <section className="syllabus-section py-5 mt-5" style={{
        background: 'linear-gradient(135deg, #e6f0ff, #cce0ff)',
        borderRadius: '20px',
        margin: '0 20px'
      }}>
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="fw-bold">JEE Main & Advanced Syllabus</h2>
            <p className="lead">Complete syllabus breakdown for effective preparation</p>
          </div>
          
          <Tab.Container defaultActiveKey="mathematics">
            <Nav variant="pills" className="justify-content-center mb-4">
              <Nav.Item>
                <Nav.Link eventKey="mathematics" className="px-4 fw-bold" style={{
                  borderRadius: '30px',
                  margin: '0 10px'
                }}>Mathematics</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="physics" className="px-4 fw-bold" style={{
                  borderRadius: '30px',
                  margin: '0 10px'
                }}>Physics</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="chemistry" className="px-4 fw-bold" style={{
                  borderRadius: '30px',
                  margin: '0 10px'
                }}>Chemistry</Nav.Link>
              </Nav.Item>
            </Nav>
            
            <Tab.Content>
              <Tab.Pane eventKey="mathematics">
                <Card className="border-0 shadow-lg">
                  <Card.Header className="py-3" style={{
                    background: 'linear-gradient(135deg, #002F6C, #005AB5)',
                    color: 'white',
                    borderBottom: 'none'
                  }}>
                    <h4 className="m-0 fw-bold">Mathematics Syllabus</h4>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <h5 className="fw-bold">Algebra</h5>
                        <ul>
                          <li>Complex Numbers & Quadratic Equations</li>
                          <li>Matrices & Determinants</li>
                          <li>Sets, Relations & Functions</li>
                          <li>Mathematical Induction</li>
                          <li>Permutations & Combinations</li>
                          <li>Binomial Theorem & Its Applications</li>
                          <li>Sequences & Series</li>
                        </ul>
                        
                        <h5 className="fw-bold mt-4">Coordinate Geometry</h5>
                        <ul>
                          <li>Straight Lines & Pair of Straight Lines</li>
                          <li>Circles & Family of Circles</li>
                          <li>Conic Sections (Parabola, Ellipse, Hyperbola)</li>
                          <li>3D Coordinate Geometry</li>
                        </ul>
                      </Col>
                      
                      <Col md={6}>
                        <h5 className="fw-bold">Calculus</h5>
                        <ul>
                          <li>Limits, Continuity & Differentiability</li>
                          <li>Applications of Derivatives</li>
                          <li>Indefinite & Definite Integrals</li>
                          <li>Applications of Integrals</li>
                          <li>Differential Equations</li>
                        </ul>
                        
                        <h5 className="fw-bold mt-4">Vector & 3D Geometry</h5>
                        <ul>
                          <li>Vector Algebra</li>
                          <li>Lines & Planes in 3D Space</li>
                        </ul>
                        
                        <h5 className="fw-bold mt-4">Statistics & Probability</h5>
                        <ul>
                          <li>Measures of Dispersion</li>
                          <li>Probability & Random Variables</li>
                          <li>Binomial Distribution</li>
                        </ul>
                      </Col>
                    </Row>
                    
                    <div className="mt-4 p-3" style={{ background: '#f0f7ff', borderRadius: '10px' }}>
                      <h5 className="fw-bold">JEE Advanced - Additional Topics</h5>
                      <p>JEE Advanced includes all JEE Main topics plus:</p>
                      <ul>
                        <li>Advanced Probability</li>
                        <li>Group Theory Introduction</li>
                        <li>Linear Programming</li>
                        <li>Advanced Calculus Problems</li>
                        <li>Differential Equations (Higher Order)</li>
                      </ul>
                    </div>
                  </Card.Body>
                </Card>
              </Tab.Pane>
              
              <Tab.Pane eventKey="physics">
                <Card className="border-0 shadow-lg">
                  <Card.Header className="py-3" style={{
                    background: 'linear-gradient(135deg, #002F6C, #005AB5)',
                    color: 'white',
                    borderBottom: 'none'
                  }}>
                    <h4 className="m-0 fw-bold">Physics Syllabus</h4>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <h5 className="fw-bold">Mechanics</h5>
                        <ul>
                          <li>Units and Measurements</li>
                          <li>Kinematics (Motion in 1D & 2D)</li>
                          <li>Laws of Motion & Friction</li>
                          <li>Work, Energy & Power</li>
                          <li>Rotational Motion</li>
                          <li>Gravitation</li>
                          <li>Properties of Solids & Liquids</li>
                        </ul>
                        
                        <h5 className="fw-bold mt-4">Thermodynamics</h5>
                        <ul>
                          <li>Thermal Properties of Matter</li>
                          <li>Laws of Thermodynamics</li>
                          <li>Heat Transfer</li>
                          <li>Kinetic Theory of Gases</li>
                        </ul>
                        
                        <h5 className="fw-bold mt-4">Waves & Sound</h5>
                        <ul>
                          <li>Simple Harmonic Motion</li>
                          <li>Wave Motion</li>
                          <li>Doppler Effect</li>
                          <li>Superposition of Waves</li>
                        </ul>
                      </Col>
                      
                      <Col md={6}>
                        <h5 className="fw-bold">Electrostatics & Magnetism</h5>
                        <ul>
                          <li>Electric Charges & Fields</li>
                          <li>Electrostatic Potential & Capacitance</li>
                          <li>Current Electricity</li>
                          <li>Moving Charges & Magnetism</li>
                          <li>Electromagnetic Induction</li>
                          <li>Alternating Currents</li>
                        </ul>
                        
                        <h5 className="fw-bold mt-4">Optics & Modern Physics</h5>
                        <ul>
                          <li>Ray Optics & Optical Instruments</li>
                          <li>Wave Optics</li>
                          <li>Dual Nature of Matter & Radiation</li>
                          <li>Atoms & Nuclei</li>
                          <li>Semiconductor Electronics</li>
                        </ul>
                      </Col>
                    </Row>
                    
                    <div className="mt-4 p-3" style={{ background: '#f0f7ff', borderRadius: '10px' }}>
                      <h5 className="fw-bold">JEE Advanced - Additional Topics</h5>
                      <p>JEE Advanced includes all JEE Main topics plus:</p>
                      <ul>
                        <li>Fluid Mechanics in detail</li>
                        <li>Experimental Physics</li>
                        <li>Advanced Mechanics Problems</li>
                        <li>Relativity Introduction</li>
                        <li>Advanced Electromagnetic Theory</li>
                      </ul>
                    </div>
                  </Card.Body>
                </Card>
              </Tab.Pane>
              
              <Tab.Pane eventKey="chemistry">
                <Card className="border-0 shadow-lg">
                  <Card.Header className="py-3" style={{
                    background: 'linear-gradient(135deg, #002F6C, #005AB5)',
                    color: 'white',
                    borderBottom: 'none'
                  }}>
                    <h4 className="m-0 fw-bold">Chemistry Syllabus</h4>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <h5 className="fw-bold">Physical Chemistry</h5>
                        <ul>
                          <li>Basic Concepts of Chemistry</li>
                          <li>States of Matter</li>
                          <li>Atomic Structure</li>
                          <li>Chemical Bonding & Molecular Structure</li>
                          <li>Chemical Thermodynamics</li>
                          <li>Solutions</li>
                          <li>Equilibrium</li>
                          <li>Redox Reactions & Electrochemistry</li>
                          <li>Chemical Kinetics</li>
                          <li>Surface Chemistry</li>
                        </ul>
                        
                        <h5 className="fw-bold mt-4">Inorganic Chemistry</h5>
                        <ul>
                          <li>Classification of Elements & Periodicity</li>
                          <li>General Principles & Processes of Isolation of Elements</li>
                          <li>Hydrogen</li>
                          <li>s-Block Elements</li>
                          <li>p-Block Elements</li>
                          <li>d & f Block Elements</li>
                          <li>Coordination Compounds</li>
                          <li>Environmental Chemistry</li>
                        </ul>
                      </Col>
                      
                      <Col md={6}>
                        <h5 className="fw-bold">Organic Chemistry</h5>
                        <ul>
                          <li>Basic Principles of Organic Chemistry</li>
                          <li>Hydrocarbons</li>
                          <li>Haloalkanes & Haloarenes</li>
                          <li>Alcohols, Phenols & Ethers</li>
                          <li>Aldehydes, Ketones & Carboxylic Acids</li>
                          <li>Nitrogen Compounds</li>
                          <li>Biomolecules</li>
                          <li>Polymers</li>
                          <li>Chemistry in Everyday Life</li>
                        </ul>
                        
                        <h5 className="fw-bold mt-4">Practical Chemistry</h5>
                        <ul>
                          <li>Qualitative Analysis</li>
                          <li>Volumetric Analysis</li>
                          <li>Chemical Preparations</li>
                        </ul>
                      </Col>
                    </Row>
                    
                    <div className="mt-4 p-3" style={{ background: '#f0f7ff', borderRadius: '10px' }}>
                      <h5 className="fw-bold">JEE Advanced - Additional Topics</h5>
                      <p>JEE Advanced includes all JEE Main topics plus:</p>
                      <ul>
                        <li>Advanced Reaction Mechanisms</li>
                        <li>Theoretical Aspects of Coordination Chemistry</li>
                        <li>Detailed Stereochemistry</li>
                        <li>Solid State Chemistry</li>
                        <li>Advanced Chemical Thermodynamics</li>
                      </ul>
                    </div>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Container>
      </section>

      {/* Previous Year Papers Section */}
      <section className="previous-papers-section py-5 mt-5">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="fw-bold">Previous Year Papers</h2>
            <p className="lead">Practice with authentic JEE Main & Advanced question papers</p>
          </div>
          
          <Row>
            <Col lg={6} className="mb-4">
              <Card className="border-0 shadow-lg h-100">
                <Card.Header className="py-3" style={{
                  background: 'linear-gradient(135deg, #002F6C, #005AB5)',
                  color: 'white',
                  borderBottom: 'none'
                }}>
                  <h4 className="m-0 fw-bold">JEE Main Papers</h4>
                </Card.Header>
                <Card.Body>
                  <div className="papers-list">
                    {previousYearPapers.map(paper => (
                      <div key={`main-${paper.year}`} className="paper-item mb-3 p-3" style={{
                        background: '#f0f7ff',
                        borderRadius: '10px'
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="fw-bold mb-0">JEE Main {paper.year}</h5>
                          <div>
                            <Button variant="outline-primary" size="sm" className="me-2">
                              <FaRegFileAlt className="me-1" /> Question Paper
                            </Button>
                            <Button variant="outline-success" size="sm">
                              <FaRegLightbulb className="me-1" /> Solutions
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-center mt-4">
                    <Button variant="primary" style={{
                      background: 'linear-gradient(135deg, #002F6C, #005AB5)',
                      border: 'none'
                    }}>
                      View All JEE Main Papers
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={6} className="mb-4">
              <Card className="border-0 shadow-lg h-100">
                <Card.Header className="py-3" style={{
                  background: 'linear-gradient(135deg, #001F49, #004190)',
                  color: 'white',
                  borderBottom: 'none'
                }}>
                  <h4 className="m-0 fw-bold">JEE Advanced Papers</h4>
                </Card.Header>
                <Card.Body>
                  <div className="papers-list">
                    {previousYearPapers.map(paper => (
                      <div key={`adv-${paper.year}`} className="paper-item mb-3 p-3" style={{
                        background: '#f0f7ff',
                        borderRadius: '10px'
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="fw-bold mb-0">JEE Advanced {paper.year}</h5>
                          <div>
                            <Button variant="outline-primary" size="sm" className="me-2">
                              <FaRegFileAlt className="me-1" /> Question Paper
                            </Button>
                            <Button variant="outline-success" size="sm">
                              <FaRegLightbulb className="me-1" /> Solutions
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-center mt-4">
                    <Button variant="primary" style={{
                      background: 'linear-gradient(135deg, #001F49, #004190)',
                      border: 'none'
                    }}>
                      View All JEE Advanced Papers
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Reference Books Section with Horizontal Scrolling */}
      <section className="reference-books-section py-5 mt-5" style={{
        background: 'linear-gradient(135deg, #e6f0ff, #cce0ff)',
        borderRadius: '20px',
        margin: '0 20px'
      }}>
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="fw-bold">Best Reference Books</h2>
            <p className="lead">Top recommended books for IIT JEE preparation</p>
          </div>
          
          <div className="book-carousel position-relative">
            <Button 
              variant="light" 
              className="carousel-control carousel-control-prev" 
              onClick={handlePrevBook}
              style={{
                position: 'absolute',
                left: -20,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}
            >
              <FaAngleLeft />
            </Button>
            
            <Row className="mx-2">
              {referenceBooks.slice(currentBookIndex, currentBookIndex + 3).map(book => (
                <Col md={4} key={book.id} className="mb-4">
                  <Card className="h-100 book-card shadow-lg" style={{
                    borderRadius: '15px',
                    overflow: 'hidden',
                    border: 'none',
                    transition: 'transform 0.3s ease'
                  }}>
                    <div style={{
                      height: '180px',
                      background: book.subject === 'Physics' 
                        ? 'linear-gradient(135deg, #002F6C, #005AB5)'
                        : book.subject === 'Mathematics' 
                        ? 'linear-gradient(135deg, #004380, #0077CC)'
                        : 'linear-gradient(135deg, #006622, #009933)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white'
                                  }}>
                                    <div className="text-center">
                                      <div style={{ fontSize: '50px' }}>
                                        {book.icon}
                                      </div>
                                      <Badge bg="light" text="dark" className="mt-2 px-3 py-2">{book.subject}</Badge>
                                    </div>
                                  </div>
                                  <Card.Body className="text-center">
                                    <h5 className="book-title fw-bold mb-1">{book.title}</h5>
                                    <p className="text-muted">by {book.author}</p>
                                    <p className="book-description">{book.description}</p>
                                    <Button variant="outline-primary" className="mt-2">View Details</Button>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                          
                          <Button 
                            variant="light" 
                            className="carousel-control carousel-control-next" 
                            onClick={handleNextBook}
                            style={{
                              position: 'absolute',
                              right: -20,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              zIndex: 10,
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            }}
                          >
                            <FaAngleRight />
                          </Button>
                        </div>
                      </Container>
                    </section>
              
                    {/* Success Stories Section */}
                    <section className="success-stories-section py-5 mt-5">
                      <Container>
                        <div className="section-header text-center mb-5">
                          <h2 className="fw-bold">Success Stories</h2>
                          <p className="lead">Meet our toppers who cracked IIT JEE with flying colors</p>
                        </div>
                        
                        <Row>
                          <Col lg={4} md={6} className="mb-4">
                            <Card className="success-story-card h-100 shadow" style={{
                              borderRadius: '15px',
                              overflow: 'hidden',
                              border: 'none'
                            }}>
                              <div className="text-center p-4" style={{ background: '#f0f7ff' }}>
                                <div style={{
                                  width: '120px',
                                  height: '120px',
                                  borderRadius: '50%',
                                  background: '#002F6C',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  margin: '0 auto',
                                  color: 'white',
                                  fontSize: '40px'
                                }}>
                                  <FaUserGraduate />
                                </div>
                              </div>
                              <Card.Body className="text-center">
                                <h5 className="fw-bold">Rahul Sharma</h5>
                                <p className="text-muted">AIR 45 | IIT Bombay</p>
                                <p>
                                  "The structured approach to problem-solving and regular mock tests helped me improve my speed and accuracy significantly."
                                </p>
                                <div className="mt-3">
                                  <Badge bg="primary" className="me-2">Physics Topper</Badge>
                                  <Badge bg="info">AIR Top 50</Badge>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                          
                          <Col lg={4} md={6} className="mb-4">
                            <Card className="success-story-card h-100 shadow" style={{
                              borderRadius: '15px',
                              overflow: 'hidden',
                              border: 'none'
                            }}>
                              <div className="text-center p-4" style={{ background: '#f0f7ff' }}>
                                <div style={{
                                  width: '120px',
                                  height: '120px',
                                  borderRadius: '50%',
                                  background: '#004380',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  margin: '0 auto',
                                  color: 'white',
                                  fontSize: '40px'
                                }}>
                                  <FaUserGraduate />
                                </div>
                              </div>
                              <Card.Body className="text-center">
                                <h5 className="fw-bold">Priya Patel</h5>
                                <p className="text-muted">AIR 78 | IIT Delhi</p>
                                <p>
                                  "The conceptual clarity I gained through the program's focused approach made advanced problems much more manageable."
                                </p>
                                <div className="mt-3">
                                  <Badge bg="primary" className="me-2">Mathematics Topper</Badge>
                                  <Badge bg="info">AIR Top 100</Badge>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                          
                          <Col lg={4} md={6} className="mb-4 mx-auto">
                            <Card className="success-story-card h-100 shadow" style={{
                              borderRadius: '15px',
                              overflow: 'hidden',
                              border: 'none'
                            }}>
                              <div className="text-center p-4" style={{ background: '#f0f7ff' }}>
                                <div style={{
                                  width: '120px',
                                  height: '120px',
                                  borderRadius: '50%',
                                  background: '#006622',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  margin: '0 auto',
                                  color: 'white',
                                  fontSize: '40px'
                                }}>
                                  <FaUserGraduate />
                                </div>
                              </div>
                              <Card.Body className="text-center">
                                <h5 className="fw-bold">Arjun Mehta</h5>
                                <p className="text-muted">AIR 120 | IIT Madras</p>
                                <p>
                                  "The personalized mentoring and detailed performance analysis helped me identify and improve my weak areas quickly."
                                </p>
                                <div className="mt-3">
                                  <Badge bg="primary" className="me-2">Chemistry Topper</Badge>
                                  <Badge bg="info">AIR Top 150</Badge>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                        
                        <div className="text-center mt-4">
                          <Button variant="primary" style={{
                            background: 'linear-gradient(135deg, #002F6C, #005AB5)',
                            border: 'none',
                            padding: '10px 25px'
                          }}>
                            Read More Success Stories
                          </Button>
                        </div>
                      </Container>
                    </section>
                    
                 
                    {/* Call to Action Section */}
                    <section className="cta-section py-5 mt-5 mb-4">
                      <Container>
                        <Card className="shadow-lg border-0" style={{
                          borderRadius: '20px',
                          overflow: 'hidden'
                        }}>
                          <Row className="g-0">
                            <Col md={8}>
                              <Card.Body className="p-5">
                                <h2 className="fw-bold mb-3">Ready to Start Your IIT JEE Journey?</h2>
                                <p className="lead mb-4">
                                  Join our comprehensive IIT JEE preparation program and take the first step towards your engineering dreams.
                                </p>
                                <div className="d-flex flex-wrap gap-3">
                                  <Button size="lg" style={{
                                    background: 'linear-gradient(135deg, #002F6C, #005AB5)',
                                    border: 'none',
                                    padding: '12px 30px'
                                  }}>
                                    <FaUniversity className="me-2" /> Enroll Now
                                  </Button>
                                  <Button variant="outline-primary" size="lg" style={{
                                    padding: '12px 30px'
                                  }}>
                                    <FaBookReader className="me-2" /> Download Free Resources
                                  </Button>
                                </div>
                                <div className="mt-4 d-flex align-items-center">
                                  <FaCertificate className="text-warning me-2" size={20} />
                                  <span className="fw-bold">Limited seats available. New batch starting soon!</span>
                                </div>
                              </Card.Body>
                            </Col>
                            <Col md={4} style={{
                              background: 'linear-gradient(135deg, #002F6C, #005AB5)',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '30px'
                            }}>
                              <div className="text-center">
                                <div style={{ fontSize: '80px' }}>
                                  <FaGraduationCap />
                                </div>
                                <h4 className="mt-3 fw-bold">Transform Your Future</h4>
                                <p>Get into top engineering institutes and unlock limitless opportunities</p>
                              </div>
                            </Col>
                          </Row>
                        </Card>
                      </Container>
                    </section>
                    
                   
                  </div>
                );
              };
              
              export default IITJEEPreparationPage;